import React, { ChangeEvent, useState, useEffect, MouseEventHandler, ReactNode } from 'react';
import {
    Wrapper,
    Container,
    Select,
    Label,
    LabelWidthReserver,
    SelectDisplay,
    IconWrapper,
    CHEVRON_SIZE,
} from './dropdown.styles';
import { InputMessage } from '../input-message';
import { Icon } from '../icon';
import { InformationType, Size } from '../../types';
import { useTheme } from '../../themes';
import { ChevronIcon } from '../../themes/base/icons/named';

interface DropdownBaseProps<T> extends Omit<
    React.HTMLAttributes<HTMLSelectElement>,
    'onChange' | 'value' | 'onClick'
> {
    label?: string;
    placeholder?: string;
    name: string;
    disabled?: boolean;
    value?: T;
    items: T[];
    keyExtractor: (item: T) => string;
    labelExtractor: (item: T) => string;
    /** The closed field's label for the picked item, when it must be shorter than the option's. */
    selectedLabelExtractor?: (item: T) => string;
    onClick?: MouseEventHandler<HTMLDivElement>;
    interactionsDisabled?: boolean;
    noWrap?: boolean; // If true, the label will not wrap and will be truncated with ellipsis
    size?: Size;
    bold?: boolean;
    message?: string | ReactNode;
    informationType?: InformationType;
    ref?: React.Ref<HTMLSelectElement>;
}

/**
 * `DropdownProps` stays an interface so consumers can still extend it. The
 * second parameter is inferred from `clearable` and only widens what
 * `onChange` receives — `DropdownProps<T>` on its own behaves exactly as it
 * did before `clearable` existed.
 */
export interface DropdownProps<T, Clearable extends boolean = false> extends DropdownBaseProps<T> {
    /**
     * Makes the placeholder a real, selectable option so the user can get back
     * to "nothing selected". Off by default, where the placeholder stays
     * `disabled` and `onChange` only ever emits an item.
     */
    clearable?: Clearable;
    onChange?: (item: Clearable extends true ? T | undefined : T) => void;
}

export function Dropdown<T, Clearable extends boolean = false>(props: DropdownProps<T, Clearable>) {
    const {
        label,
        name,
        disabled,
        value: initialValue,
        onChange = () => {},
        items,
        keyExtractor,
        labelExtractor,
        selectedLabelExtractor = labelExtractor,
        placeholder = '',
        onClick,
        interactionsDisabled = false,
        noWrap = false,
        size = Size.NORMAL,
        bold = false,
        message,
        informationType = InformationType.INFO,
        clearable = false,
        ref,
        // The conditional `onChange` type is for callers only; the body works
        // with the widest shape.
    } = props as DropdownBaseProps<T> & {
        clearable?: boolean;
        onChange?: (item: T | undefined) => void;
    };
    const theme = useTheme();
    const [value, setValue] = useState<T | undefined>(initialValue);

    const messageId = `${name}-message`;
    const hasError = !!message && informationType === InformationType.ERROR;

    useEffect(() => {
        setValue(initialValue);
    }, [initialValue]);

    const selectedKey = value != null ? keyExtractor(value) : '';

    const handleChange = (e: ChangeEvent<HTMLSelectElement>) => {
        const selIndex = e.target.selectedIndex;
        // index 0 is our placeholder
        if (selIndex === 0) {
            setValue(undefined);
            if (clearable) {
                onChange(undefined);
            }
            return;
        }
        const picked = items[selIndex - 1];
        setValue(picked);
        onChange(picked);
    };

    return (
        <Wrapper>
            <Container
                $hasLabel={Boolean(label)}
                $disabled={Boolean(disabled)}
                $interactionsDisabled={interactionsDisabled}
                $size={size}
                $hasMessage={!!message}
                $informationType={informationType}
            >
                <Select
                    ref={ref}
                    disabled={disabled}
                    name={name}
                    id={name}
                    onChange={handleChange}
                    value={selectedKey}
                    $interactionsDisabled={interactionsDisabled}
                    $size={size}
                    aria-invalid={hasError}
                    aria-describedby={message ? messageId : undefined}
                >
                    <option value="" disabled={!clearable}>
                        {placeholder}
                    </option>
                    {items.map((item) => {
                        const key = keyExtractor(item);
                        return (
                            <option key={key} value={key}>
                                {labelExtractor(item)}
                            </option>
                        );
                    })}
                </Select>

                <SelectDisplay
                    onClick={onClick}
                    $noWrap={noWrap}
                    $size={size}
                    $bold={bold}
                    $hasValue={value != null}
                    $hasLabel={Boolean(label)}
                >
                    {value != null ? selectedLabelExtractor(value) : placeholder}
                </SelectDisplay>

                <IconWrapper $size={size}>
                    <Icon
                        icon={ChevronIcon}
                        size={CHEVRON_SIZE}
                        color={theme.foreground.alternative}
                    />
                </IconWrapper>

                {label && (
                    <>
                        <LabelWidthReserver aria-hidden>{label}</LabelWidthReserver>
                        <Label
                            htmlFor={name}
                            $size={size}
                            $elevated={value != null || Boolean(placeholder)}
                        >
                            {label}
                        </Label>
                    </>
                )}
            </Container>

            <InputMessage id={messageId} message={message} informationType={informationType} />
        </Wrapper>
    );
}
